import { create } from 'zustand';
import type { ImageUpload, VisualDiscoveryResponse, ImageAnalysis } from '../types/visual.types';
import type { EnrichedTrack } from '../types/track.types';

interface VisualState {
  // Data
  images: ImageUpload[];
  results: EnrichedTrack[];
  imageAnalyses: ImageAnalysis[];
  interpretation: string | null;

  // UI
  loading: boolean;
  error: string | null;
  cacheHit: boolean;
  responseTimeMs: number;

  // Actions
  addImage: (file: File) => void;
  removeImage: (id: string) => void;
  clearImages: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setResults: (response: VisualDiscoveryResponse) => void;
  clearResults: () => void;

  // Computed
  isValid: () => boolean;
}

export const useVisualStore = create<VisualState>((set, get) => ({
  // Initial state
  images: [],
  results: [],
  imageAnalyses: [],
  interpretation: null,
  loading: false,
  error: null,
  cacheHit: false,
  responseTimeMs: 0,

  // Actions
  addImage: (file: File) => {
    const state = get();
    if (state.images.length >= 4) {
      return; // Max 4 images
    }

    const newImage: ImageUpload = {
      id: crypto.randomUUID(),
      file,
      preview: URL.createObjectURL(file),
      uploaded: false,
    };

    set({ images: [...state.images, newImage] });
  },

  removeImage: (id: string) => {
    const state = get();
    const image = state.images.find(img => img.id === id);
    if (image) {
      URL.revokeObjectURL(image.preview); // Clean up object URL
    }
    set({ images: state.images.filter(img => img.id !== id) });
  },

  clearImages: () => {
    const state = get();
    state.images.forEach(img => URL.revokeObjectURL(img.preview));
    set({ images: [] });
  },

  setLoading: (loading: boolean) => set({ loading }),

  setError: (error: string | null) => set({ error }),

  setResults: (response: VisualDiscoveryResponse) =>
    set({
      results: response.recommendations,
      imageAnalyses: response.image_analyses,
      interpretation: response.combined_interpretation,
      cacheHit: response.cache_hit,
      responseTimeMs: response.response_time_ms,
      loading: false,
      error: null,
    }),

  clearResults: () =>
    set({
      results: [],
      imageAnalyses: [],
      interpretation: null,
      cacheHit: false,
      responseTimeMs: 0,
    }),

  // Computed
  isValid: () => {
    const state = get();
    return state.images.length >= 1 && state.images.length <= 4;
  },
}));
